import React, { useState, useRef, useCallback } from "react";
import Logo from "./Logo";
import FileBrowser from "./FileBrowser";

const API = process.env.REACT_APP_API_URL || "";

const ACCEPTED_TYPES = [
  ".pdf", ".pptx", ".ppt", ".xlsx", ".xls", ".csv",
  ".doc", ".docx", ".txt", ".zip", ".png", ".jpg", ".jpeg",
];

function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function fileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  const icons = {
    pdf: "\u{1F4D1}", pptx: "\u{1F4CA}", ppt: "\u{1F4CA}",
    xlsx: "\u{1F4C8}", xls: "\u{1F4C8}", csv: "\u{1F4C8}",
    doc: "\u{1F4DD}", docx: "\u{1F4DD}", txt: "\u{1F4DD}",
    zip: "\u{1F4E6}", png: "\u{1F5BC}", jpg: "\u{1F5BC}", jpeg: "\u{1F5BC}",
  };
  return icons[ext] || "\u{1F4CE}";
}

const UploadPortal = ({ token, onLogout }) => {
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);          // queued files
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);       // completed uploads
  const [uploaderName, setUploaderName] = useState("");
  const [fileBrowserKey, setFileBrowserKey] = useState(0); // used to trigger refresh
  const inputRef = useRef(null);

  // Drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...dropped]);
  }, []);

  const handleSelect = (e) => {
    const selected = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selected]);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    const newResults = [];

    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("uploader_name", uploaderName || "Anonymous");

      try {
        const res = await fetch(`${API}/api/upload`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` },
          body: form,
        });
        if (res.ok) {
          const data = await res.json();
          newResults.push({ name: file.name, status: "success", detail: data.gcs_path });
        } else if (res.status === 401) {
          // Token expired — force re-login
          onLogout();
          return;
        } else {
          const err = await res.json().catch(() => ({}));
          newResults.push({ name: file.name, status: "error", detail: err.detail || "Upload failed" });
        }
      } catch {
        newResults.push({ name: file.name, status: "error", detail: "Network error" });
      }
    }

    setResults(newResults);
    setFiles([]);
    setUploading(false);
    setFileBrowserKey((k) => k + 1); // trigger file browser refresh
  };

  return (
    <div style={styles.page}>
      {/* Top bar */}
      <header style={styles.header}>
        <Logo height={32} />
        <button onClick={onLogout} style={styles.logoutBtn}>Log out</button>
      </header>

      <main style={styles.main}>
        <h1 style={styles.heading}>Shop Masters — Upload Data Assets</h1>
        <p style={styles.subheading}>
          Drag and drop your files below, or click to browse. Accepted formats:{" "}
          {ACCEPTED_TYPES.join(", ")}
        </p>

        {/* Uploader name */}
        <input
          type="text"
          placeholder="Your name (required)"
          value={uploaderName}
          onChange={(e) => setUploaderName(e.target.value)}
          style={styles.nameInput}
          required
        />

        {/* Drop zone */}
        <div
          style={{
            ...styles.dropzone,
            borderColor: dragActive ? "#ff4f8b" : "#d1d5db",
            background: dragActive ? "rgba(255,79,139,0.04)" : "#fafafa",
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleSelect}
            style={{ display: "none" }}
          />
          <div style={styles.dropIcon}>{dragActive ? "\u{1F4E5}" : "\u{1F4C1}"}</div>
          <p style={styles.dropText}>
            {dragActive
              ? "Drop files here"
              : "Drag & drop files here, or click to browse"}
          </p>
          <p style={styles.dropHint}>Maximum file size: 100 MB</p>
        </div>

        {/* Queued files */}
        {files.length > 0 && (
          <div style={styles.fileList}>
            <h3 style={styles.listTitle}>
              Ready to upload ({files.length} file{files.length > 1 ? "s" : ""})
            </h3>
            {files.map((f, i) => (
              <div key={i} style={styles.fileRow}>
                <span style={styles.fileIcon}>{fileIcon(f.name)}</span>
                <span style={styles.fileName}>{f.name}</span>
                <span style={styles.fileSize}>{formatBytes(f.size)}</span>
                <button onClick={() => removeFile(i)} style={styles.removeBtn}>
                  ✕
                </button>
              </div>
            ))}
            {!uploaderName.trim() && (
              <p style={styles.nameWarning}>Please enter your name before uploading.</p>
            )}
            <button
              onClick={handleUpload}
              disabled={uploading || !uploaderName.trim()}
              style={{
                ...styles.uploadBtn,
                opacity: uploading || !uploaderName.trim() ? 0.6 : 1,
              }}
            >
              {uploading ? "Uploading…" : `Upload ${files.length} file${files.length > 1 ? "s" : ""}`}
            </button>
          </div>
        )}

        {/* Upload results */}
        {results.length > 0 && (
          <div style={styles.resultsList}>
            <h3 style={styles.listTitle}>Upload Results</h3>
            {results.map((r, i) => (
              <div
                key={i}
                style={{
                  ...styles.resultRow,
                  borderLeft: `4px solid ${r.status === "success" ? "#22c55e" : "#ef4444"}`,
                }}
              >
                <span>{r.status === "success" ? "\u2705" : "\u274C"}</span>
                <span style={styles.fileName}>{r.name}</span>
                <span style={styles.resultDetail}>
                  {r.status === "success" ? "Uploaded successfully" : r.detail}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* File browser — view & download uploaded documents */}
        <FileBrowser key={fileBrowserKey} token={token} onLogout={onLogout} />
      </main>

      <footer style={styles.footer}>
        All files are encrypted in transit (TLS) and stored securely in Google Cloud.
      </footer>
    </div>
  );
};

/* ---------- styles ---------- */
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#f3f4f6",
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 32px",
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  logoutBtn: {
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 600,
    color: "#6b7280",
    background: "transparent",
    border: "1.5px solid #d1d5db",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  main: {
    flex: 1,
    maxWidth: 720,
    width: "100%",
    margin: "0 auto",
    padding: "40px 24px",
  },
  heading: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    color: "#1a1a2e",
  },
  subheading: {
    margin: "8px 0 20px",
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.5,
  },
  nameInput: {
    width: "100%",
    padding: "12px 16px",
    fontSize: 14,
    border: "1.5px solid #e0e0e0",
    borderRadius: 10,
    outline: "none",
    fontFamily: "inherit",
    marginBottom: 16,
    boxSizing: "border-box",
  },
  dropzone: {
    border: "2px dashed #d1d5db",
    borderRadius: 14,
    padding: "48px 24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  dropIcon: { fontSize: 40, marginBottom: 8 },
  dropText: { margin: 0, fontSize: 15, color: "#374151", fontWeight: 500 },
  dropHint: { margin: "8px 0 0", fontSize: 12, color: "#9ca3af" },
  fileList: {
    marginTop: 24,
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  listTitle: {
    margin: "0 0 14px",
    fontSize: 15,
    fontWeight: 600,
    color: "#1a1a2e",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid #f3f4f6",
  },
  fileIcon: { fontSize: 20 },
  fileName: {
    flex: 1,
    fontSize: 14,
    color: "#1f2937",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileSize: { fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" },
  removeBtn: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: 14,
    padding: "4px 8px",
  },
  uploadBtn: {
    marginTop: 16,
    width: "100%",
    padding: "14px",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "#ff4f8b",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  resultsList: {
    marginTop: 24,
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 8,
    marginBottom: 8,
    background: "#fafafa",
  },
  resultDetail: { fontSize: 12, color: "#6b7280" },
  nameWarning: { margin: "0 0 4px", fontSize: 13, color: "#ef4444", textAlign: "center" },
  footer: {
    textAlign: "center",
    padding: "20px 24px",
    fontSize: 12,
    color: "#9ca3af",
    borderTop: "1px solid #e5e7eb",
  },
};

export default UploadPortal;
